/**
 * ManualTreeSidebar Component
 *
 * Sidebar navigation with hierarchical tree structure for manual sections.
 * Supports expand/collapse, active state highlighting, and keyboard navigation.
 * Story 1-3: ManualTreeSidebar
 */

import { useState, useCallback, useEffect, useMemo } from 'react';
import { ManualTreeNode } from './ManualTreeNode';
import type { TreeNode } from '../../lib/manualApi';

interface ManualTreeSidebarProps {
  tree: TreeNode[];
  activeSlug: string | undefined;
  onNavigate: (slug: string) => void;
  isLoading?: boolean;
  error?: Error | null;
}

/**
 * Skeleton loader for tree sidebar
 */
function TreeSkeleton() {
  return (
    <div className="p-4 space-y-2" role="status" aria-label="Laden...">
      {[1, 2, 3, 4, 5].map((i) => (
        <div key={i} className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-3/4" />
          {i % 2 === 0 && (
            <div className="ml-4 mt-1 space-y-1">
              <div className="h-5 bg-gray-100 rounded w-2/3" />
              <div className="h-5 bg-gray-100 rounded w-1/2" />
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

/**
 * Empty state when no sections are available
 */
function EmptyState() {
  return (
    <div className="p-4 text-center text-gray-500">
      <p className="text-sm">Geen secties beschikbaar</p>
    </div>
  );
}

/**
 * Error state component
 */
function ErrorState({ error }: { error: Error }) {
  return (
    <div className="p-4 text-center" role="alert">
      <p className="text-sm text-red-600 mb-2">
        Fout bij laden van handleiding
      </p>
      <p className="text-xs text-gray-500">{error.message}</p>
    </div>
  );
}

/**
 * Get all part node IDs from tree for initial expansion
 */
function getPartNodeIds(tree: TreeNode[]): Set<number> {
  const ids = new Set<number>();
  for (const node of tree) {
    if (node.sectionType === 'part') {
      ids.add(node.id);
    }
  }
  return ids;
}

/**
 * ManualTreeSidebar - Main tree sidebar component
 */
export function ManualTreeSidebar({
  tree,
  activeSlug,
  onNavigate,
  isLoading = false,
  error = null,
}: ManualTreeSidebarProps) {
  // Memoize tree node IDs to detect changes
  const treeNodeIds = useMemo(
    () => tree.map((n) => n.id).join(','),
    [tree]
  );

  // Track expanded nodes by ID
  const [expandedNodes, setExpandedNodes] = useState<Set<number>>(() => {
    return getPartNodeIds(tree);
  });

  // Sync expandedNodes when tree changes (re-expand parts)
  useEffect(() => {
    const partIds = getPartNodeIds(tree);
    setExpandedNodes((prev) => {
      // Merge: keep existing expanded nodes that still exist, add new parts
      const validNodeIds = new Set(tree.map((n) => n.id));
      const next = new Set<number>();

      // Keep previously expanded nodes if they still exist
      for (const id of prev) {
        if (validNodeIds.has(id)) {
          next.add(id);
        }
      }

      // Add new part nodes
      for (const id of partIds) {
        next.add(id);
      }

      return next;
    });
  }, [treeNodeIds, tree]);

  // Toggle node expansion
  const toggleNode = useCallback((nodeId: number) => {
    setExpandedNodes((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return next;
    });
  }, []);

  // Loading state
  if (isLoading) {
    return (
      <aside
        className="w-64 flex-shrink-0 bg-white border-r border-gray-200 overflow-y-auto"
        data-testid="manual-tree-sidebar"
      >
        <TreeSkeleton />
      </aside>
    );
  }

  // Error state
  if (error) {
    return (
      <aside
        className="w-64 flex-shrink-0 bg-white border-r border-gray-200"
        data-testid="manual-tree-sidebar"
      >
        <ErrorState error={error} />
      </aside>
    );
  }

  // Empty state
  if (!tree || tree.length === 0) {
    return (
      <aside
        className="w-64 flex-shrink-0 bg-white border-r border-gray-200"
        data-testid="manual-tree-sidebar"
      >
        <EmptyState />
      </aside>
    );
  }

  return (
    <aside
      className="w-64 flex-shrink-0 bg-white border-r border-gray-200 overflow-y-auto"
      data-testid="manual-tree-sidebar"
    >
      <nav className="py-2" aria-label="Handleiding navigatie">
        <h2 className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
          Inhoudsopgave
        </h2>
        <ul role="tree" aria-label="Handleiding navigatie">
          {tree.map((node) => (
            <ManualTreeNode
              key={node.id}
              node={node}
              depth={0}
              activeSlug={activeSlug}
              isExpanded={expandedNodes.has(node.id)}
              expandedNodes={expandedNodes}
              onToggle={toggleNode}
              onNavigate={onNavigate}
            />
          ))}
        </ul>
      </nav>
    </aside>
  );
}
